/**
 * @file Tracking.ts
 * @description Tracking events for Google Analytics 4, and Hotjar.
 *
 * @module Tracking
 */
import { pipe } from 'fp-ts/function';
import * as Http from './http/HttpClient';
import { notifyHB } from './Observability';
/**
 * Tracking events for Google Analytics 4
 *
 * @example
 *
 * import { Tracking } from '@catawiki/fe';
 *
 * Tracking.pushGATrackingEvent(
 *  'ga-event',
 *  {
 *    value: 'key',
 *   },
 *   'event label',
 * );
 *
 */
export const pushGATrackingEvent = (event, eventPayload = {}, eventLabel) => {
    if (!window.dataLayer) {
        notifyHB(new Error('Unable to track the event. DataLayer not found'), {
            fingerprint: 'google_analytics',
        });
        return;
    }
    window.dataLayer.push({
        event,
        ...(eventLabel && { event_label: eventLabel }),
        ...eventPayload,
    });
};
/**
 * @ignore
 * From the Hotjar documentation:
 *
 * If there's a chance that your event would fire on the page before your Hotjar Tracking Code,
 * you would need to add this line of code right before your event code
 *
 * https://help.hotjar.com/hc/en-us/articles/4412561401111-How-to-send-Events-to-Hotjar-with-Google-Tag-Manager-#how-to-create-a-custom-html-tag-to-send-an-event-to-hotjar
 */
const createHotjarQueue = () => {
    window.hj =
        window.hj ||
            ((...args) => {
                window.hj.q = window.hj.q || [];
                window.hj.q.push(args);
            });
};
/**
 * Tracking events for Hotjar
 *
 * @example
 *
 * import { Tracking } from '@catawiki/fe';
 *
 * Tracking.pushHotjarAction('trigger', 'key');
 *
 */
export const pushHotjarAction = (action, key) => {
    createHotjarQueue();
    window.hj(action, key);
};
/**
 * Helper method to push dataLayer and dispatch a new click event
 * with properties of the original one.
 *
 * @example
 * onClick (e, url) => {
 *   clickWithDataLayer(e, url, {
 *     event: 'ga-event',
 *     eventCategory: 'breadcrumbEvent',
 *     eventAction: 'breadcrumbClicked',
 *     eventLabel: 0,
 *   });
 * }
 */
export const clickWithDataLayer = (event, url, dataLayer) => {
    // GA might be not available: adblockers, private mode in Firefox, etc.
    // https://developer.mozilla.org/en-US/Firefox/Privacy/Tracking_Protection
    if (!(window.ga && window.ga.loaded)) {
        // If the event was already prevented, we must go to the promised URL
        if (event.defaultPrevented) {
            window.location.assign(url);
        }
        return true;
    }
    const { ctrlKey, metaKey } = 'nativeEvent' in event ? event.nativeEvent : event;
    const isNewTab = ctrlKey || metaKey;
    if (!isNewTab)
        event.preventDefault();
    window.dataLayer.push(isNewTab
        ? dataLayer
        : {
            ...dataLayer,
            eventCallback: () => {
                window.location.assign(url);
            },
        });
    return false;
};
export const VISITOR_EVENT = 'visitor';
/**
 * Track visitor event
 *
 * Porbably you don't need to use this function directly. Instead, use the `useTrackVisitorEvents` hook
 * for tracking visitor events in React components.
 *
 */
export const trackVisitorEvent = ({ userAgent, anonymousId, locale, event, userId, params, }) => pipe(Http.postJson(`/user_tracking/api/v1/external/visitor`, {
    user_agent: userAgent,
    anonymous_id: anonymousId,
    locale,
    event: event || VISITOR_EVENT,
    user_id: Number(userId),
    ...params,
}), Http.send());
